/**
 * @fileoverview Declarative React Router data-router tree and authentication redirects.
 * @provides The browser router, protected loaders, nested shells, and lazy boundaries.
 * @usedBy The React application entrypoint.
 * @uses React Router, session API, layouts, and lazily imported route modules.
 */
import { createBrowserRouter, redirect } from "react-router";
import { loadSession } from "./lib/api";
import { AppShell } from "./layouts/app-shell";
import { PublicShell } from "./layouts/public-shell";
import { ErrorPage } from "./pages/error-page";

async function protectedLoader(role: "teacher" | "admin") {
  const user = await loadSession();
  if (!user) throw redirect("/login");
  if (user.passkeyRequired) throw redirect("/setup-passkey");
  if (user.role !== role) throw redirect(user.role === "admin" ? "/admin" : "/dashboard");
  return user;
}
async function protectedAnyLoader() { const user = await loadSession(); if (!user) throw redirect("/login"); if (user.passkeyRequired) throw redirect("/setup-passkey"); return user; }
async function passkeySetupLoader() { const user = await loadSession(); if (!user) throw redirect("/login"); if (!user.passkeyRequired) throw redirect(user.role === "admin" ? "/admin" : "/dashboard"); return user; }

const profileRoute = { path: "profile", lazy: async () => { const module = await import("./pages/profile-page"); return { Component: module.ProfilePage, loader: module.profileLoader }; } };
const landingRoute = (path: string) => ({ path, lazy: async () => { const module = await import("./pages/landing-page"); return { Component: module.LandingPage, loader: module.landingLoader }; } });
const docsRoute = { path: "docs/:section?", lazy: async () => { const module = await import("./pages/docs-page"); return { Component: module.DocsPage, loader: module.docsLoader }; } };

export const router = createBrowserRouter([{
  path: "/",
  errorElement: <ErrorPage />,
  children: [
    { element: <PublicShell />, children: [
      { index: true, loader: () => redirect("/login") },
      { path: "login", lazy: async () => ({ Component: (await import("./pages/login-page")).LoginPage }) },
      { path: "verify-otp", lazy: async () => ({ Component: (await import("./pages/verify-page")).VerifyPage }) },
      { path: "setup-passkey", loader: passkeySetupLoader, lazy: async () => ({ Component: (await import("./pages/passkey-setup-page")).PasskeySetupPage }) },
    ] },
    { loader: () => protectedLoader("teacher"), element: <AppShell />, children: [
      { path: "dashboard", lazy: async () => { const module = await import("./pages/dashboard-page"); return { Component: module.DashboardPage, loader: module.dashboardLoader }; } },
      { path: "referrals", lazy: async () => { const module = await import("./pages/referrals-page"); return { Component: module.ReferralsPage, loader: module.referralsLoader }; } },
      { path: "owner", lazy: async () => { const module = await import("./pages/campaign-owner-page"); return { Component: module.CampaignOwnerPage, loader: module.campaignOwnerLoader }; } },
    ] },
    { path: "admin", loader: () => protectedLoader("admin"), element: <AppShell />, children: [
      { index: true, lazy: async () => { const module = await import("./pages/admin-page"); return { Component: module.AdminPage, loader: module.adminLoader }; } },
      { path: "users", lazy: async () => { const module = await import("./pages/users-page"); return { Component: module.UsersPage, loader: module.usersLoader }; } },
      { path: "teachers", loader: () => redirect("/admin/users") },
      { path: "campaigns", lazy: async () => { const module = await import("./pages/campaigns-page"); return { Component: module.CampaignsPage, loader: module.campaignsLoader }; } },
      { path: "campaigns/:id", lazy: async () => { const module = await import("./pages/campaign-detail-page"); return { Component: module.CampaignDetailPage, loader: module.campaignDetailLoader }; } },
    ] },
    { loader: protectedAnyLoader, element: <AppShell />, children: [profileRoute, docsRoute] },
    landingRoute(":campaignSlug/:teacherSlug"),
    landingRoute(":campaignSlug"),
    { path: "*", element: <ErrorPage /> },
  ],
}]);
